/**
 * ConnectedAppsPage — Landlord / Manager
 *
 * Route: /settings/connected-apps
 *
 * Lists the user's connected apps — AI assistants connected over MCP and the
 * Resida listing extension — and allows revoking them. An MCP grant shows its
 * tool count and a Manage link; the extension (kind 'listing-extension', BR-29)
 * shows what it can do instead, and has no tool permissions to manage.
 * Rendered inside SettingsLayout.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LISTING_EXTENSION_CAPABILITIES } from '@resida/shared';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { listMcpGrants, revokeMcpGrant, type GrantRecord } from '../lib/api';
import { Button } from '../components/ui/button';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard';

/**
 * The API returns `permissionsUrl` as a full browser path (e.g. `/landlord/settings/...`), but this
 * app's BrowserRouter has `basename="/landlord"`, so `<Link to>` needs the router-relative path —
 * passing the full path double-prefixes it (`/landlord/landlord/settings/...`).
 */
function toRouterPath(url: string): string {
  return url.replace(/^\/landlord(?=\/)/, '');
}

// The MCP endpoint is served by the API at the domain root (no /landlord
// prefix — see docs/features/mcp-infrastructure/specification.md §"API
// Endpoints"), which is always same-origin with this web app in every
// environment (dev, sandbox, prod all run web + API as one monolith).
const MCP_SERVER_URL = `${window.location.origin}/mcp`;

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function ConnectedAppsPage() {
  const [grants, setGrants] = useState<GrantRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<GrantRecord | null>(null);
  const { copied, copy } = useCopyToClipboard();

  const loadGrants = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listMcpGrants();
      setGrants(data.grants);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connected apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const handleRevoke = async (grant: GrantRecord) => {
    try {
      setRevoking(grant.clientId);
      setError(null);
      await revokeMcpGrant(grant.clientId);
      setGrants((prev) => prev.filter((g) => g.clientId !== grant.clientId));
      setConfirmRevoke(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setRevoking(null);
    }
  };

  return (
    <SettingsLayout>
      <div
        data-testid="connected-apps-page"
        className="px-[var(--page-padding-x)]"
      >
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-foreground m-0">Connected Apps</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-0">
              Apps you've connected to your Resida account.
            </p>
          </div>

          <div
            data-testid="connected-apps-connect-instructions"
            className="mb-6 p-4 bg-card rounded-lg border border-border"
          >
            <h2 className="m-0 mb-1 text-sm font-semibold text-foreground">
              Connect a new app
            </h2>
            <p className="m-0 mb-3 text-sm text-muted-foreground">
              In your AI assistant (e.g. Claude Desktop or Claude.ai), add a custom connector
              using the server URL below. You'll be asked to sign in and approve the access
              it's requesting.
            </p>
            <div className="flex gap-2 items-center">
              <code
                id="mcp-server-url-display"
                data-testid="mcp-server-url"
                className="flex-1 px-3 py-2 bg-accent/50 border border-border rounded-md text-sm font-mono text-foreground select-all break-all"
              >
                {MCP_SERVER_URL}
              </code>
              <Button
                variant="outline"
                size="sm"
                data-testid="mcp-server-url-copy"
                onClick={() => copy(MCP_SERVER_URL)}
                className="shrink-0 min-h-[44px]"
                aria-label={`Copy MCP server URL: ${MCP_SERVER_URL}`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          {error && (
            <div
              role="alert"
              className="flex justify-between items-center p-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-6"
            >
              <p className="text-sm text-destructive m-0">{error}</p>
              <Button
                variant="outline"
                size="sm"
                data-testid="connected-apps-retry"
                onClick={loadGrants}
                className="min-h-[44px]"
              >
                Retry
              </Button>
            </div>
          )}

          {loading ? (
            <div
              data-testid="connected-apps-loading"
              role="status"
              aria-live="polite"
              className="space-y-3"
            >
              {[1, 2].map((i) => (
                <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : grants.length === 0 ? (
            <div
              data-testid="connected-apps-empty"
              className="text-center py-10 px-6 bg-card rounded-lg border border-border"
            >
              <h2 className="text-lg font-semibold text-foreground m-0 mb-2">No Connected Apps</h2>
              <p className="text-sm text-muted-foreground m-0">
                You haven't connected any apps yet.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {grants.map((grant) => (
                <div
                  key={grant.clientId}
                  data-testid={`grant-item-${grant.clientId}`}
                  className="flex flex-col sm:flex-row sm:justify-between sm:items-start px-5 py-4 bg-card rounded-lg border border-border gap-3 sm:gap-4"
                >
                  <div className="flex items-start gap-4 min-w-0">
                    {grant.logoUri ? (
                      <img
                        src={grant.logoUri}
                        alt={`${grant.clientName} logo`}
                        className="w-10 h-10 rounded-md object-contain flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center flex-shrink-0 text-muted-foreground text-xs font-semibold">
                        {grant.clientName.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="m-0 text-base font-medium text-foreground truncate">
                        {grant.clientName}
                      </h3>
                      <p className="m-0 mt-0.5 text-xs text-muted-foreground">
                        Last authorized: {formatDate(grant.lastConsentedAt)}
                      </p>
                      {grant.kind === 'listing-extension' ? (
                        <ul
                          data-testid={`grant-capabilities-${grant.clientId}`}
                          aria-label={`What ${grant.clientName} can do`}
                          className="m-0 mt-1 p-0 list-none space-y-0.5 text-sm text-muted-foreground"
                        >
                          {LISTING_EXTENSION_CAPABILITIES.can.map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p
                          data-testid={`grant-permission-count-${grant.clientId}`}
                          className="m-0 mt-1 text-xs text-muted-foreground"
                        >
                          {grant.activePermissionCount} tool{grant.activePermissionCount !== 1 ? 's' : ''} authorized
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 sm:self-start">
                    {grant.permissionsUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`grant-manage-${grant.clientId}`}
                        asChild
                        className="min-h-[44px] flex-1 sm:flex-none"
                      >
                        <Link to={toRouterPath(grant.permissionsUrl)}>Manage</Link>
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`grant-revoke-${grant.clientId}`}
                      onClick={() => setConfirmRevoke(grant)}
                      disabled={revoking === grant.clientId}
                      className="min-h-[44px] flex-1 sm:flex-none"
                    >
                      {revoking === grant.clientId ? 'Revoking...' : 'Revoke'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation dialog */}
      {confirmRevoke && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="revoke-dialog-title"
        >
          <div className="bg-card rounded-lg border border-border max-w-sm w-full p-6">
            <h3 id="revoke-dialog-title" className="text-lg font-semibold text-foreground m-0 mb-2">
              Revoke access for {confirmRevoke.clientName}?
            </h3>
            <p className="text-sm text-muted-foreground m-0 mb-6">
              This will immediately sign them out and revoke all active tokens.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                data-testid="revoke-dialog-cancel"
                onClick={() => setConfirmRevoke(null)}
                disabled={revoking !== null}
                className="min-h-[44px]"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                data-testid="revoke-dialog-confirm"
                onClick={() => handleRevoke(confirmRevoke)}
                disabled={revoking !== null}
                className="min-h-[44px]"
              >
                {revoking ? 'Revoking...' : 'Revoke Access'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </SettingsLayout>
  );
}

export default ConnectedAppsPage;
